import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DocumentStorageCategory, DEFAULT_DOCUMENT_STORAGE_CATEGORIES } from '../types/documentStorage';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export function useDocumentStorageCategories() {
  const [categories, setCategories] = useState<DocumentStorageCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const { user } = useAuth();

  const initializeDefaultCategoriesInternal = useCallback(async (): Promise<void> => {
    if (!user?.organizationId || !user.uid) {
      throw new Error('Authentication required');
    }

    try {
      const promises = DEFAULT_DOCUMENT_STORAGE_CATEGORIES.map(category => 
        addDoc(collection(db, 'documentStorageCategories'), {
          ...category,
          organizationId: user.organizationId,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      );

      await Promise.all(promises);
      toast.success('Alapértelmezett kategóriák létrehozva');
    } catch (error) {
      console.error('Error initializing default categories:', error);
      throw new Error('Alapértelmezett kategóriák létrehozása sikertelen');
    }
  }, [user?.organizationId, user?.uid]);

  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'documentStorageCategories'),
      where('organizationId', '==', user.organizationId),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      } as DocumentStorageCategory));

      setCategories(categoriesData);
      setLoading(false);

      // Auto-initialize default categories if none exist and not yet initialized
      if (categoriesData.length === 0 && !initialized) {
        setInitialized(true);
        try {
          await initializeDefaultCategoriesInternal();
        } catch (error) {
          console.error('Auto-initialization failed:', error);
        }
      }
    }, (error) => {
      console.error('Error fetching document storage categories:', error);
      toast.error('Hiba a kategóriák betöltésekor');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.organizationId, initializeDefaultCategoriesInternal, initialized]);

  const initializeDefaultCategories = async (): Promise<void> => {
    if (initialized) return;
    setInitialized(true);
    return initializeDefaultCategoriesInternal();
  };

  const createCategory = async (data: {
    name: string;
    description?: string;
    color: string;
    icon: string;
  }): Promise<string> => {
    if (!user?.organizationId || !user.uid) {
      throw new Error('Authentication required');
    }

    try {
      const docRef = await addDoc(collection(db, 'documentStorageCategories'), {
        name: data.name,
        description: data.description || '',
        color: data.color,
        icon: data.icon,
        organizationId: user.organizationId,
        isDefault: false,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast.success('Kategória sikeresen létrehozva');
      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error('Kategória létrehozása sikertelen');
    }
  };

  const updateCategory = async (id: string, data: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
  }): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('Authentication required');
    }

    try {
      const docRef = doc(db, 'documentStorageCategories', id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });

      toast.success('Kategória sikeresen frissítve');
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error('Kategória frissítése sikertelen');
    }
  };

  const deleteCategory = async (id: string): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('Authentication required');
    }

    try {
      // Instead of deleting, we mark as inactive
      const docRef = doc(db, 'documentStorageCategories', id);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });

      toast.success('Kategória sikeresen törölve');
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error('Kategória törlése sikertelen');
    }
  };

  return {
    categories,
    loading,
    initializeDefaultCategories,
    createCategory,
    updateCategory,
    deleteCategory
  };
}
